import type { ComparisonSpec } from './comparison.js';

/**
 * Why overhead power lines are aluminium and house wiring is copper.
 *
 * Density, resistivity and melting point are handbook constants at 20 °C. The
 * third row is derived from the first two: for equal conductance a conductor's
 * mass scales with resistivity × density, so relative to copper aluminium needs
 * (2.65 / 1.68) × (2.70 / 8.96) = 0.47 of the mass. Copper wins two rows of four
 * and still loses the decision, which is the whole point of the figure.
 */
export function comparisonFixture(width = 640): ComparisonSpec {
  return {
    id: 'overhead-conductor-choice',
    family: 'comparison',
    view: 'table',
    viewport: { width, height: width <= 340 ? 500 : 490 },
    subject: 'Overhead line conductor',
    question: 'Aluminium or copper for an overhead line?',
    options: [
      { id: 'aluminium', label: 'Aluminium', detail: 'hard-drawn' },
      { id: 'copper', label: 'Copper', detail: 'annealed' },
    ],
    criteria: [
      { id: 'density', label: 'Density', unit: 'g/cm³', first: 2.7, second: 8.96, better: 'lower', decimals: 2 },
      { id: 'resistivity', label: 'Resistivity at 20 °C', unit: 'nΩ·m', first: 26.5, second: 16.8, better: 'lower', decimals: 1 },
      { id: 'mass-per-conductance', label: 'Mass for equal conductance', unit: '× copper', first: 0.47, second: 1, better: 'lower', decisive: true, decimals: 2 },
      { id: 'melting-point', label: 'Melting point', unit: '°C', first: 660, second: 1085, better: 'higher' },
    ],
    verdictOptionId: 'aluminium',
    verdictReason: 'A span carries its own weight before it carries current, so mass per unit of conductance is the binding constraint.',
    title: 'Two of four criteria go to copper, and aluminium still wins',
    subtitle: 'Each row states which direction is better, so the winner is computed; one criterion outranks the tally.',
    provenance: 'Resistivity and density: CRC Handbook values at 20 °C. Mass for equal conductance derived from those two figures.',
  };
}
